import {useState, useCallback, useEffect, useRef, useMemo} from 'react'
import {serverDirectionFor, recordServerDirection} from '@/utils/serverDirection'
import {useNavigate} from 'react-router'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {tripService, isRouteHashError, isPaymentMethodUnusableError, PAYMENT_METHOD_UNUSABLE_MESSAGE} from '@/services/tripService'
import {useTripDetails} from '@/hooks/useTrips'
import {useTriDirection, isNearAirport} from '@/hooks/useAirportDetector'
import {getAirportIdsFor} from '@/utils/airports'
import {ensureAirports} from '@/hooks/useAirports'
import {BOOKING_CONSTRAINTS, TRIP_DIRECTIONS} from '@/constants/booking'
import {tripNow, formatTripLocalTime, roundToNext5Min, getMinLeadTimeStr} from '@/utils/dateUtils'
import {useAppConfig} from '@/hooks/useAppConfig'
import {tripCancelPolicy} from '@/utils/functions'
import {reverseGeocodePostalCode} from '@/customComponents/uiComponents/global/PlaceAutocomplete.jsx'

const EDIT_CACHE_PREFIX = 'hopn-edit-trip-'

const SECTION_FIELDS = {
    destination: ['origin', 'destination', 'stop_overs'],
    // Split parts of the destination editor (separate origin vs drop-off modals)
    // so each card gates on just its own field(s).
    dropoff: ['destination', 'stop_overs'],
    passengers: ['passenger_types', 'child_seats'],
    luggage: ['luggage'],
    car: ['vehicle_type_id'],
    special_request: ['special_request', 'having_pet', 'quiet_ride'],
}

function extractLocation(loc) {
    if (!loc) return {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}
    if (typeof loc === 'string') return {address: loc, lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}
    return {address: loc.address || '', lat: loc.lat || '', lng: loc.lng || '', city: loc.city || '', state: loc.state || '', country: loc.country || '', postal_code: loc.postal_code || ''}
}

// ── calculate-cost / update input builders (shared by the preview recalcs + submit) ──
const toPassengerArr = (map) => Object.entries(map).map(([id, qty]) => ({passenger_type_id: parseInt(id), quantity: qty})).filter(p => p.quantity > 0)
const toChildSeatArr = (map) => Object.entries(map).map(([id, qty]) => ({child_seat_type_id: parseInt(id), quantity: qty})).filter(s => s.quantity > 0)
const toLuggageArr = (std, over) => [
    ...Object.entries(std).map(([id, qty]) => ({luggage_type_id: parseInt(id), quantity: qty})),
    ...Object.entries(over).map(([id, qty]) => ({luggage_type_id: parseInt(id), quantity: qty})),
].filter(l => l.quantity > 0)
// postal_code is REQUIRED by the backend on every location (same rule calculateRoute
// already follows in tripService). It was missing here, so the /trips/{id} update sent
// origin/destination without one — "The origin postal code is required." The value is
// carried through the whole edit state already (normalizeLocation keeps it off the trip,
// DestinationSection writes it from PlaceAutocomplete's reverse-geocode fallback); only
// this payload builder dropped it.
const toLocationPayload = (l) => ({lat: parseFloat(l.lat), lng: parseFloat(l.lng), address: l.address, city: l.city || '', state: l.state || '', country: l.country || '', postal_code: l.postal_code || ''})

// trip_direction from a pair of live locations (matches useTriDirection semantics).
const directionFor = (orig, dest) => {
    // The server's direction (admin switch on) wins; otherwise our own coordinate match.
    const server = serverDirectionFor(orig, dest)
    if (server) return server.direction
    const from = Boolean(isNearAirport(orig?.lat, orig?.lng))
    const to = Boolean(isNearAirport(dest?.lat, dest?.lng)) && !from
    return from ? TRIP_DIRECTIONS.FROM_AIRPORT : to ? TRIP_DIRECTIONS.TO_AIRPORT : TRIP_DIRECTIONS.OTHERS
}

// The free-modification allowance is exhausted (used ≥ total, total > 0). While true the
// WHOLE edit form is read-only — every field/section is disabled even if the backend still
// lists it in editable_fields. Matches the header + submit-button "at limit" check.
const isAtModificationLimit = (policy) => {
    if (!policy) return false
    const used = Number(policy.modifications_used) || 0
    const total = Number(policy.free_modifications) || 0
    return total > 0 && used >= total
}
const NO_MODIFICATION_REASON = 'There is no modification available now'

/**
 * @param {string|number} rideId
 * @param {{defaultFirstCard?: boolean}} [opts]
 *   `defaultFirstCard` (default true) pre-selects the passenger's FIRST saved card when
 *   the trip names none. That is a FORM default — the edit and confirm pages need
 *   something selected for the passenger to submit, and neither has a client-side gate
 *   that would stop a confirm going out with `payment_method_id: null`.
 *
 *   READ-ONLY callers pass false. On a page that only displays the booking, pre-selecting
 *   a card the trip does not name is a false statement about what will be charged — see
 *   RideTripView.
 */
export default function useEditTrip(rideId, {defaultFirstCard = true} = {}) {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    // Live-poll the server trip so status/driver/payment changes show without a reload.
    // The user's un-submitted edits are NOT in this cache — they live in `previewOverlay`
    // (local state) below and are merged in at read, so the poll can't clobber them.
    const {data, isLoading: isTripLoading} = useTripDetails(rideId, {live: true})
    // Gate on /config/app the same way the booking modal does: subscribe so this
    // hook re-renders (and the min-lead-time validation recomputes) once the config
    // lands, and so the page holds the loading state until BOOKING_CONSTRAINTS can
    // read the real values from /config/app instead of the hardcoded fallbacks.
    const {data: appConfig, isLoading: isConfigLoading} = useAppConfig()

    // The un-submitted edit preview (recalculated price + edited fields), kept in memory
    // only — reload / leaving the page drops it, exactly like the rest of the edit state.
    // `applyPreview` fills it; the main submit and discard clear it.
    const [previewOverlay, setPreviewOverlay] = useState(null)
    // Reset if the trip id changes without a remount (defensive; routes normally remount).
    useEffect(() => { setPreviewOverlay(null) }, [rideId])

    // Displayed trip = live server copy with the edit overlay laid on top. The overlay
    // wins only for the fields the user edited; everything else (status, driver, …) stays
    // live from the poll — so a server-side cancellation shows mid-edit without losing edits.
    const trip = useMemo(() => {
        const server = data?.trip
        if (!server || !previewOverlay) return server
        return {
            ...server,
            ...previewOverlay.fields,
            selectables: previewOverlay.vehicleTypes
                ? {...server.selectables, vehicle_types: previewOverlay.vehicleTypes}
                : server.selectables,
        }
    }, [data?.trip, previewOverlay])

    const isLoading = isTripLoading || isConfigLoading

    const getSectionPolicy = useCallback((section) => {
        const policy = trip?.edit_policy
        if (!policy) return {canEdit: true, blockedReason: null}
        // Out of free modifications → nothing is editable, regardless of editable_fields.
        if (isAtModificationLimit(policy)) return {canEdit: false, blockedReason: NO_MODIFICATION_REASON}
        const fields = SECTION_FIELDS[section] || [section]
        const editable = policy.editable_fields || []
        const locked = policy.locked_reasons || {}
        // destination + passengers open if any of their sub-fields is editable
        // (passengers additionally overlays the locked sub-section inside its popup)
        if (section === 'destination' || section === 'dropoff' || section === 'passengers' || section === 'special_request') {
            if (fields.some(f => editable.includes(f))) return {canEdit: true, blockedReason: null}
            return {canEdit: false, blockedReason: locked[fields[0]] || policy.blocked_reason || null}
        }
        const firstBlocked = fields.find(f => !editable.includes(f))
        if (!firstBlocked) return {canEdit: true, blockedReason: null}
        return {canEdit: false, blockedReason: locked[firstBlocked] || policy.blocked_reason || null}
    }, [trip])

    const getFieldPolicy = useCallback((field) => {
        const policy = trip?.edit_policy
        if (!policy) return {canEdit: true, blockedReason: null}
        // Out of free modifications → nothing is editable, regardless of editable_fields.
        if (isAtModificationLimit(policy)) return {canEdit: false, blockedReason: NO_MODIFICATION_REASON}
        const editable = policy.editable_fields || []
        const locked = policy.locked_reasons || {}
        if (!editable.includes(field)) {
            return {canEdit: false, blockedReason: locked[field] || policy.blocked_reason || null}
        }
        return {canEdit: true, blockedReason: null}
    }, [trip])

    // Delegates to the shared gate (utils/functions) so the edit page, the details page and
    // the trips list can't drift — it also blocks once the ride is under way.
    const getCancelPolicy = useCallback(() => tripCancelPolicy(trip), [trip])

    // ── Live state ──
    const [destinations, setDestinations] = useState({origin: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, destination: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, stopOvers: []})
    const [passengers, setPassengers] = useState({})
    const [childSeats, setChildSeats] = useState({})
    const [luggage, setLuggage] = useState({})
    const [oversizedLuggage, setOversizedLuggage] = useState({})

    // ── Committed state ──
    const [committedDestinations, setCommittedDestinations] = useState({origin: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, destination: {address: '', lat: '', lng: '', city: '', state: '', country: '', postal_code: ''}, stopOvers: []})
    const [committedPassengers, setCommittedPassengers] = useState({})
    const [committedChildSeats, setCommittedChildSeats] = useState({})
    const [committedLuggage, setCommittedLuggage] = useState({})
    const [committedOversizedLuggage, setCommittedOversizedLuggage] = useState({})

    // Vehicle
    const [selectedCar, setSelectedCar] = useState(null)
    const [committedCarTypeId, setCommittedCarTypeId] = useState(null)
    const [availableCars, setAvailableCars] = useState([])

    // Other
    const [selectedCardId, setSelectedCardId] = useState(null)
    const [savedCards, setSavedCards] = useState([])
    const [specialRequest, setSpecialRequest] = useState({notes: '', caringPet: false, quietRide: false})
    const [contact, setContact] = useState({full_name: '', email: '', phone: ''})
    // Empty = "not known yet", NOT 0%. A live trip fills it from
    // selected_vehicle.gratuity_percentage on hydration; a Draft has no selected_vehicle, so it
    // stays empty until the first calculate-cost response reports the gratuity the backend
    // applied. Do NOT seed it from DRIVER_TIP_OPTIONS[0]: that getter is empty until
    // /config/app lands, so at mount it yielded String(undefined) — which then went out on
    // calculate-cost as a NaN gratuity and priced the trip untipped.
    const [driverTip, setDriverTip] = useState('')
    // Same reasoning as driverTip above — and this one is what closeEdit restores, so seeding it
    // from the empty-at-mount DRIVER_TIP_OPTIONS getter put the literal "undefined" back into
    // the gratuity dropdown every time a section modal was closed.
    const [committedDriverTip, setCommittedDriverTip] = useState('')
    const [flightNumber, setFlightNumber] = useState('')
    // Applied coupon — kept so section/tip recalcs preserve the discount, and so the
    // main submit persists it. Seeded from the trip; updated by the coupon form.
    const [couponCode, setCouponCode] = useState('')

    // Selectables — from the trip detail only (`?include_selectables=true`). This hook
    // also backs the read-only /rides/:id view, so it deliberately does NOT fetch the
    // /selectables/* lists: those are for the booking flow and the confirm page (see
    // useConfirmTrip), and App.jsx keeps them off /rides* on purpose.
    const [passengerTypes, setPassengerTypes] = useState([])
    const [childSeatTypes, setChildSeatTypes] = useState([])
    const [luggageTypes, setLuggageTypes] = useState([])

    // UI
    const [editingSection, setEditingSection] = useState(null)
    const [passengerView, setPassengerView] = useState('main')
    const [passengerOnly, setPassengerOnly] = useState(null) // 'passengers' | 'childseats' | null
    const [destinationPart, setDestinationPart] = useState(null) // 'origin' | 'dropoff' | null
    const [luggageOversizedMode, setLuggageOversizedMode] = useState(false)
    const [carBreakdownId, setCarBreakdownId] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isRecalculating, setIsRecalculating] = useState(false)
    const [sectionSaveError, setSectionSaveError] = useState(null)
    const [bookingError, setBookingError] = useState(null)
    const [showSuccess, setShowSuccess] = useState(false)
    const [expanded, setExpanded] = useState(new Set())
    const [confirmStep, setConfirmStep] = useState(0)
    const [showCancelPopup, setShowCancelPopup] = useState(false)
    const [showSRPopup, setShowSRPopup] = useState(false)
    const [infoPopup, setInfoPopup] = useState(null)
    const [showFlightPrompt, setShowFlightPrompt] = useState(false)
    const [flightFinalizeError, setFlightFinalizeError] = useState(null)

    // DateTime
    const [liveDatetime, setLiveDatetime] = useState({date: null, time: null, hour: null, minute: null})
    // The min-lead-time message is a call-to-action, not an automatic state: it must fire only after
    // the user actively (re)picks a date/time — never on first reload off the trip's existing
    // scheduled_at (which DateTimeCard commits into liveDatetime on mount). Set true by the user's
    // date/time pick handlers; the mount-commit and smart-time apply leave it false.
    const [dateTimeTouched, setDateTimeTouched] = useState(false)

    // Minimum booking lead-time validation (from /config/app booking.start_time_buffer).
    // The selected pickup must be at least MINIMUM_BOOKING_LEAD_TIME_MINUTES ahead of "now"
    // (tripNow() = APP_TIMEZONE wall-clock). Both Dates live in the same wall-clock frame, so
    // their epoch difference is the true wall-clock gap. Returns a warning string or null.
    const minLeadTimeError = useMemo(() => {
        // Runtime CTA: silent until the user actively picks a date/time (dateTimeTouched). Mirrors the
        // booking-flow wheel step (travelingDateTime.jsx `validateLeadTime`) EXACTLY: the picked time must
        // be at least the backend min lead time PLUS a 10-min FRONTEND buffer ahead of now (tripNow() =
        // APP_TIMEZONE wall-clock, same frame as the picker). The message shows the backend lead time;
        // "earliest available" is that buffered minAllowed rounded up to the next 5-min slot.
        if (!dateTimeTouched) return null
        if (!liveDatetime?.time) return null
        const selected = new Date(liveDatetime.time)
        if (isNaN(selected.getTime())) return null
        const minAllowed = new Date(tripNow().getTime() + (BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES + 10) * 60000)
        if (selected.getTime() >= minAllowed.getTime()) return null
        const earliest = roundToNext5Min(minAllowed)
        return `For quick bookings, please contact ${BOOKING_CONSTRAINTS.CONTACT_NUMBER}. Online booking is only available ${getMinLeadTimeStr()} in advance (earliest available: ${formatTripLocalTime(earliest)}).`
    }, [liveDatetime, appConfig, dateTimeTouched])

    // Shared flight state
    const [sharedFlightNumber, setSharedFlightNumber] = useState('')
    const [sharedFlightDetails, setSharedFlightDetails] = useState(null)
    const [sharedFlightLoading, setSharedFlightLoading] = useState(false)
    const [sharedFlightError, setSharedFlightError] = useState('')
    const [sharedTimeApplied, setSharedTimeApplied] = useState(false)
    // True once a flight lookup fails because the PROVIDER is unreachable (blocked key,
    // rate limit, 5xx, network) — NOT a clean "no such flight". Set by DateTimeSection's
    // lookups; stays true (across date/direction clears) until a lookup SUCCEEDS, so a
    // known outage keeps trusting the booking's saved flight instead of deadlocking.
    const [sharedFlightUnavailable, setSharedFlightUnavailable] = useState(false)

    // Flight-required gate value shared by the /edit + /confirm submit buttons. The gate is
    // satisfied only once the flight has been VALIDATED via the smart-suggestion lookup
    // (sharedFlightDetails is set only on a successful flight-API response) — typing a number
    // alone is not enough, so "Please enter your flight number" persists until the API returns
    // a matching flight. Exception: the trip's OWN saved flight, left untouched, was already
    // validated at booking, so it counts until the user changes it (or a direction flip clears it).
    // OUTAGE ESCAPE HATCH: if the provider is unreachable, we CAN'T re-validate — so a booking
    // that already has a saved flight is trusted rather than trapping the user behind a gate
    // that can never clear (see project note "flight gate deadlock on provider outage").
    const hasFlightNumber = !!sharedFlightDetails
        || (!!trip?.flight_number && sharedFlightNumber === trip.flight_number)
        // OUTAGE ESCAPE HATCH: sharedFlightUnavailable is set ONLY when the user clicks
        // "Proceed anyway" on the provider-unavailable card, so any flight number they
        // have entered is acknowledged — open the gate. Keyed off sharedFlightNumber (not
        // trip.flight_number) so it also works for a typed number / a trip with no saved flight.
        || (sharedFlightUnavailable && !!sharedFlightNumber)

    // Editing the field invalidates the flight COMPLETELY — the number on screen is no longer
    // the one the API confirmed, so every derived value goes with it. flightNumber (the
    // committed value the update payload reads) is cleared too: without that, a half-typed
    // edit would leave the OLD flight in the submit payload while the field showed something
    // else. Dropping the outage acknowledgement matters as well — it was granted for a
    // specific number, not for whatever replaces it.
    const handleFlightNumberChange = useCallback((v) => {
        setSharedFlightNumber(v)
        setSharedFlightDetails(null)
        setSharedFlightError('')
        setSharedFlightUnavailable(false)
        setSharedTimeApplied(false)
        setFlightNumber('')
    }, [])

    // The ✕ on the flight pill: same wipe, but the field empties too.
    const clearFlight = useCallback(() => {
        handleFlightNumberChange('')
    }, [handleFlightNumberChange])

    // Hints
    const [dateHint, setDateHint] = useState(true)
    const [flightHint, setFlightHint] = useState(false)
    const [smartTimePopup, setSmartTimePopup] = useState(false)
    const skipHintTransition = useRef(false)
    const dateTimeSectionRef = useRef(null)
    const applySmartTimeRef = useRef(null)
    const prevAirportRef = useRef(null)
    const populatedRef = useRef(false)
    // Set by the Smart Time modal (DateTimeSection) to true while the looked-up flight is an
    // airport MISMATCH the user hasn't acknowledged ("Proceed anyway" not clicked). Read on
    // modal close to decide whether the unconfirmed flight should be dropped.
    const flightDropRef = useRef(false)

    // Airport detection
    const {isFromAirport, isToAirport} = useTriDirection(
        committedDestinations.origin.lat, committedDestinations.origin.lng,
        committedDestinations.destination.lat, committedDestinations.destination.lng
    )
    const tripDirection = isFromAirport ? TRIP_DIRECTIONS.FROM_AIRPORT : isToAirport ? TRIP_DIRECTIONS.TO_AIRPORT : TRIP_DIRECTIONS.OTHERS
    const isAirportTrip = isFromAirport || isToAirport

    // Hint effects
    useEffect(() => {
        if (prevAirportRef.current === isFromAirport) return
        prevAirportRef.current = isFromAirport
        setDateHint(true)
        setFlightHint(false)
    }, [isFromAirport])

    useEffect(() => {
        if (isFromAirport && !sharedFlightDetails && !flightHint) {
            setFlightHint(true)
            setDateHint(false)
        }
    }, [sharedFlightDetails])

    const handleDateChange = useCallback(() => {
        setDateTimeTouched(true)
        setSharedFlightNumber('')
        setSharedFlightDetails(null)
        setSharedFlightError('')
        setSharedTimeApplied(false)
        setDateHint(false)
        if (isFromAirport && !skipHintTransition.current) setFlightHint(true)
    }, [isFromAirport])

    // User actively (re)picked a time — arms the min-lead-time CTA (fires even if the same time is reselected).
    const handleTimeChange = useCallback(() => {
        setDateTimeTouched(true)
        setSharedTimeApplied(false)
    }, [])

    const handleSmartTimeSelect = useCallback((recommendedTime) => {
        if (applySmartTimeRef.current) {
            skipHintTransition.current = true
            applySmartTimeRef.current(recommendedTime)
            setTimeout(() => { skipHintTransition.current = false }, 0)
        }
        setSmartTimePopup(false)
        setDateHint(false)
        setFlightHint(false)
    }, [isFromAirport])

    // Close the Smart Time modal. If it's being dismissed on an unacknowledged airport mismatch
    // (user never clicked "Proceed anyway"), the flight was never confirmed — drop it so it
    // leaves the input and the flight-required gate re-applies.
    const closeSmartTime = useCallback(() => {
        if (flightDropRef.current) {
            setSharedFlightNumber('')
            setSharedFlightDetails(null)
            setSharedFlightError('')
            setSharedTimeApplied(false)
        }
        flightDropRef.current = false
        setSmartTimePopup(false)
    }, [])
    // DateTimeSection reports here whether closing now should drop the flight (unacknowledged mismatch).
    const setFlightDropOnClose = useCallback((v) => { flightDropRef.current = v }, [])

    // ── Populate ──
    useEffect(() => {
        if (!trip || populatedRef.current) return
        populatedRef.current = true
        // A trip the server typed from the airport zones already carries its direction; seed it
        // so the first paint follows the server, not our coordinate guess.
        if (trip.trip_direction_source === 'zone' && trip.trip_direction) {
            recordServerDirection(extractLocation(trip.origin), extractLocation(trip.destination), {trip_direction: trip.trip_direction, airport: trip.airport || null})
        }

        const orig = extractLocation(trip.origin)
        const dest = extractLocation(trip.destination)
        const stops = (trip.stop_overs || []).map((s, i) => ({id: Date.now() + i, ...extractLocation(s.location || s)}))
        const destData = {origin: orig, destination: dest, stopOvers: stops}
        setDestinations(destData)
        setCommittedDestinations(destData)

        const passMap = {}
        ;(trip.selected_passengers || []).forEach(p => { if (p.passenger_type_id) passMap[p.passenger_type_id] = parseInt(p.quantity) || 0 })
        setPassengers(passMap)
        setCommittedPassengers(passMap)

        const seatMap = {}
        ;(trip.selected_child_seats || []).forEach(s => { if (s.child_seat_type_id) seatMap[s.child_seat_type_id] = parseInt(s.quantity) || 0 })
        setChildSeats(seatMap)
        setCommittedChildSeats(seatMap)

        const lugMap = {}, overMap = {}
        ;(trip.selected_luggage || []).forEach(l => { const id = l.luggage_type_id, qty = parseInt(l.quantity) || 0; if (l.category === 'Oversized') overMap[id] = qty; else lugMap[id] = qty })
        setLuggage(lugMap); setCommittedLuggage(lugMap)
        setOversizedLuggage(overMap); setCommittedOversizedLuggage(overMap)

        const sv = trip.selected_vehicle
        if (sv) { setSelectedCar(sv); setCommittedCarTypeId(sv.vehicle_type_id ?? sv.id) }

        const sel = trip.selectables
        if (sel) {
            if (sel.passenger_types) setPassengerTypes(sel.passenger_types)
            if (sel.child_seat_types) setChildSeatTypes(sel.child_seat_types)
            if (sel.luggage_types) setLuggageTypes(sel.luggage_types)
            const vtArr = (sel.vehicle_types || []).map(v => ({...v, vehicle_type_id: v.vehicle_type_id ?? v.id, vehicle_name: v.vehicle_name ?? v.name, vehicle_image: v.vehicle_image ?? v.image_url}))
            if (vtArr.length > 0) setAvailableCars(vtArr)
        }

        const payMethods = trip.passenger?.payment_methods || []
        setSavedCards(payMethods)
        // Prefer whatever the trip already points at — the expanded object, else the raw id
        // it carries. The first-saved-card fallback is a FORM default and is skipped for
        // read-only callers, which must not imply a choice the passenger never made.
        setSelectedCardId(
            trip.selected_payment_method?.id
            ?? trip.payment_method_id
            ?? (defaultFirstCard && payMethods.length > 0 ? payMethods[0].id : null)
        )
        setSpecialRequest({notes: trip.trip_notes || '', caringPet: trip.having_pet || false, quietRide: trip.quiet_ride || false})
        setContact({full_name: trip.passenger?.full_name || '', email: trip.passenger?.email || '', phone: trip.passenger?.phone || ''})
        // A LIVE trip carries its gratuity on the priced vehicle (same place RideDetails /
        // CompletedRideDetails read it). A DRAFT has no priced vehicle — but it DOES carry
        // `gratuity_percentage` at the top level of the trip, so read that before falling
        // back to the calculate-cost response (adopted in applyPreview). Same third read as
        // useConfirmTrip — keep the two in step.
        // Only a real (> 0) value; see the note there.
        const tipPct = trip.selected_vehicle?.pricing?.gratuity_percentage
            ?? trip.selected_vehicle?.gratuity_percentage
            ?? trip.gratuity_percentage
        if (tipPct) { setDriverTip(String(tipPct)); setCommittedDriverTip(String(tipPct)) }
        setFlightNumber(trip.flight_number || '')
        if (trip.flight_number) setSharedFlightNumber(trip.flight_number)
        setCouponCode(trip.discount_code || trip.coupon_code || '')

        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith(EDIT_CACHE_PREFIX) && !key.startsWith(`${EDIT_CACHE_PREFIX}${rideId}-`)) sessionStorage.removeItem(key)
        })
    }, [trip, rideId, defaultFirstCard])

    // ── Modal handlers ──
    const openEdit = useCallback((s, {passengerOnly = null, oversized = false, part = null} = {}) => { setSectionSaveError(null); setPassengerView('main'); setPassengerOnly(passengerOnly); setLuggageOversizedMode(oversized); setDestinationPart(part); setCarBreakdownId(null); setEditingSection(s) }, [])

    // Closing a section modal DISCARDS the in-progress edit: revert live state to the committed
    // baseline. Only Done saves. (No unsaved-changes prompt / "Discard Changes" button.)
    const closeEdit = useCallback(() => {
        setDestinations({...committedDestinations}); setPassengers({...committedPassengers}); setChildSeats({...committedChildSeats})
        setLuggage({...committedLuggage}); setOversizedLuggage({...committedOversizedLuggage})
        const cc = availableCars.find(c => String(c.vehicle_type_id ?? c.id) === String(committedCarTypeId))
        if (cc) setSelectedCar(cc)
        setDriverTip(committedDriverTip); setSectionSaveError(null); setCarBreakdownId(null); setEditingSection(null)
    }, [committedDestinations, committedPassengers, committedChildSeats, committedLuggage, committedOversizedLuggage, availableCars, committedCarTypeId, committedDriverTip])

    // ── Local price preview (NO persist) ──
    // DONE / tip / coupon no longer save. They re-price the CURRENT selection via
    // calculate-route → calculate-cost and write an OPTIMISTIC trip into the cache, so the
    // cards + breakdown update exactly as before. The real save happens only on the main
    // submit. A reload (without submitting) refetches the server trip and reverts — a true preview.
    const applyPreview = useCallback((vehicleTypes, dir) => {
        const mapped = (vehicleTypes || []).map(v => ({...v, vehicle_type_id: v.vehicle_type_id ?? v.id, vehicle_name: v.vehicle_name ?? v.name, vehicle_image: v.vehicle_image ?? v.image_url}))
        if (mapped.length) setAvailableCars(mapped)
        const selId = selectedCar?.vehicle_type_id ?? selectedCar?.id
        // Fall back to the FIRST option the quote returned when nothing is chosen yet.
        // A Draft arrives with no vehicle, and a previously-picked type can drop out of
        // the list when passengers/luggage change — either way the summary showed
        // "No vehicle selected" and made the user open Select Vehicle and press Done
        // before anything could be priced or confirmed.
        const selVehicle = mapped.find(v => String(v.vehicle_type_id) === String(selId)) || mapped[0] || null
        if (selVehicle) { setSelectedCar(selVehicle); setCommittedCarTypeId(selVehicle.vehicle_type_id) }
        // The gratuity % arrives HERE, on the calculate-cost response — not in the trip. On a
        // Draft the first quote is sent with no gratuity, so this is how the dropdown learns the
        // percentage the backend applied (and thereafter what gets sent back). Adopted straight
        // from the response rather than waiting for the trip-hydration effect to notice it.
        // Only a real (> 0) value: a 0 means the vehicle was priced without a gratuity, and
        // echoing it would pin every later quote untipped.
        const respTip = selVehicle?.pricing?.gratuity_percentage ?? selVehicle?.gratuity_percentage
        if (respTip) { setDriverTip(String(respTip)); setCommittedDriverTip(String(respTip)) }
        // Rebuild the trip's selected_* lists from live selections + type metadata so the
        // summary cards (which read the cache) reflect the pending change without a save.
        const selected_passengers = passengerTypes.filter(t => (passengers[t.id] || 0) > 0).map(t => ({...t, passenger_type_id: t.id, quantity: passengers[t.id]}))
        const selected_child_seats = childSeatTypes.filter(t => (childSeats[t.id] || 0) > 0).map(t => ({...t, child_seat_type_id: t.id, quantity: childSeats[t.id]}))
        const selected_luggage = [
            ...luggageTypes.filter(t => t.category === 'Standard' && (luggage[t.id] || 0) > 0).map(t => ({...t, luggage_type_id: t.id, quantity: luggage[t.id]})),
            ...luggageTypes.filter(t => t.category === 'Oversized' && (oversizedLuggage[t.id] || 0) > 0).map(t => ({...t, luggage_type_id: t.id, quantity: oversizedLuggage[t.id]})),
        ]
        // Store the preview as a local overlay (NOT the ['trip', rideId] cache, which the
        // 2s poll owns). `trip` above merges these fields over the live server trip.
        setPreviewOverlay({
            fields: {
                ...(selVehicle ? {selected_vehicle: selVehicle} : {}),
                selected_passengers, selected_child_seats, selected_luggage,
                origin: {...destinations.origin}, destination: {...destinations.destination},
                stop_overs: destinations.stopOvers.map(s => ({location: {address: s.address, lat: s.lat, lng: s.lng, city: s.city || '', state: s.state || '', country: s.country || ''}})),
                trip_direction: dir,
            },
            vehicleTypes: mapped.length ? mapped : null,
        })
    }, [selectedCar, passengerTypes, childSeatTypes, luggageTypes, passengers, childSeats, luggage, oversizedLuggage, destinations])

    // Refresh the route FIRST (so calculate-cost has a valid route_hash — user's precaution),
    // then calculate-cost. Returns {success, error?}. `tip`/`coupon` override current state.
    // The pickup time THIS PAGE has chosen — the picker's live value, else the trip's
    // saved one. /calculate-route is quoted against it so the duration reflects predicted
    // traffic for that window, and because the route_hash is bucketed by the window, a
    // time change here produces a fresh hash for calculate-cost and the submit.
    // Deliberately NOT the booking flow's sessionStorage time: that belongs to a
    // different session and would quote this trip against someone else's pickup.
    const livePickupAt = useCallback(
        () => (liveDatetime?.time ? liveDatetime.time.replace('T', ' ') : (trip?.scheduled_at || null)),
        [liveDatetime, trip?.scheduled_at],
    )

    const runPreviewRecalc = useCallback(async ({tip, coupon} = {}) => {
        const {origin, destination, stopOvers} = destinations
        const dir = directionFor(origin, destination)
        const passArr = toPassengerArr(passengers)
        const seatArr = toChildSeatArr(childSeats)
        const lugArr = toLuggageArr(luggage, oversizedLuggage)
        const effTip = tip != null ? tip : driverTip
        const effCoupon = (coupon !== undefined ? coupon : couponCode) || null
        const getHash = async () => {
            const r = await tripService.calculateRoute(origin, destination, stopOvers, {scheduledPickupAt: livePickupAt()})
            return r.success ? {hash: r.data?.route_hash ?? r.data?.routeHash} : {error: r.error || 'Could not calculate the route.'}
        }
        // Surge is time-windowed, so the preview price has to be quoted for the pickup time the
        // update will save — otherwise the card shows a base fare the server won't honour. NO
        // trip_id here: this is a live trip, not a draft, and the API doc only defines trip_id
        // for draft (return/rebook) quoting.
        const quoteOpts = {
            scheduledAt: liveDatetime?.time ? liveDatetime.time.replace('T', ' ') : (trip?.scheduled_at || null),
        }
        const first = await getHash()
        if (first.error) return {success: false, error: first.error}
        let cost = await tripService.calculateCost(first.hash, passArr, seatArr, lugArr, effCoupon, effTip, dir, quoteOpts)
        if (!cost.success && isRouteHashError(cost)) {
            const retry = await getHash()
            if (!retry.error) cost = await tripService.calculateCost(retry.hash, passArr, seatArr, lugArr, effCoupon, effTip, dir, quoteOpts)
        }
        if (!cost.success) return {success: false, error: cost.error || 'Could not calculate the price.'}
        applyPreview(cost.data || [], dir)
        return {success: true}
    }, [destinations, passengers, childSeats, luggage, oversizedLuggage, driverTip, couponCode, applyPreview, liveDatetime, trip?.scheduled_at])

    // ── Section DONE: re-price (NO server save), then commit locally ──
    const handleSectionDone = useCallback(async (sectionName) => {
        setIsRecalculating(true); setSectionSaveError(null)
        const res = await runPreviewRecalc()
        if (!res.success) { setSectionSaveError(res.error || 'Failed to update price.'); setIsRecalculating(false); return }
        // Commit the just-edited section (baseline for dirty-check / RouteCard / revert / submit).
        if (sectionName === 'destination') {
            // If this edit flips the trip direction (e.g. toAirport ⇄ fromAirport), the
            // previously-entered flight no longer matches the new leg. Drop it so the
            // flight-required gate + airport-mismatch validation re-apply at runtime —
            // exactly like a reload. Date/time are intentionally preserved.
            const prevDir = directionFor(committedDestinations.origin, committedDestinations.destination)
            const nextDir = directionFor(destinations.origin, destinations.destination)
            setCommittedDestinations({...destinations})
            setFlightNumber('')
            if (prevDir !== nextDir) {
                setSharedFlightNumber('')
                setSharedFlightDetails(null)
                setSharedFlightError('')
                setSharedTimeApplied(false)
                setFlightHint(nextDir === TRIP_DIRECTIONS.FROM_AIRPORT)
            }
        }
        if (sectionName === 'passengers') { setCommittedPassengers({...passengers}); setCommittedChildSeats({...childSeats}) }
        if (sectionName === 'luggage') { setCommittedLuggage({...luggage}); setCommittedOversizedLuggage({...oversizedLuggage}) }
        if (sectionName === 'car') { setCommittedCarTypeId(selectedCar?.vehicle_type_id ?? selectedCar?.id); setCommittedDriverTip(driverTip) }
        setConfirmStep(0)
        setEditingSection(null)
        setIsRecalculating(false)
    }, [destinations, committedDestinations, passengers, childSeats, luggage, oversizedLuggage, selectedCar, driverTip, runPreviewRecalc])

    // A location the user never touched came from GET /trips/{id}, which returns no
    // postal_code — so `postal_code: ''` would still fail the backend's "required" check.
    // Backfill from the coordinates with the same reverse-geocode PlaceAutocomplete uses
    // when a city-level place has no postal code of its own. Locations the user DID pick
    // already carry one and short-circuit.
    const ensurePostalCodes = useCallback(async (d) => {
        const fill = async (loc) => {
            if (!loc || loc.postal_code || !loc.lat || !loc.lng) return loc
            const pc = await reverseGeocodePostalCode(parseFloat(loc.lat), parseFloat(loc.lng))
            return pc ? {...loc, postal_code: pc} : loc
        }
        const [origin, destination, stopOvers] = await Promise.all([
            fill(d.origin),
            fill(d.destination),
            Promise.all((d.stopOvers || []).map(fill)),
        ])
        return {origin, destination, stopOvers}
    }, [])

    // Structural selections persisted by the main submit only. Sourced from committed
    // state — this is the single point that saves the accumulated section edits that
    // DONE previewed but never persisted.
    //
    // `all: true` skips the editable_fields gate entirely, for the CONFIRM path — see
    // doConfirm. The edit path passes the gate as normal. `dests` lets the caller hand in
    // postal-code-backfilled destinations (see ensurePostalCodes).
    const structuralUpdateFields = useCallback((editable, {all = false, dests} = {}) => {
        const can = (f) => all || (editable || []).includes(f)
        const d = dests || committedDestinations
        const p = {}
        if (can('origin')) p.origin = toLocationPayload(d.origin)
        if (can('destination')) p.destination = toLocationPayload(d.destination)
        if (can('stop_overs')) p.stop_overs = d.stopOvers.filter(s => s.address && s.lat && s.lng).map(toLocationPayload)
        if (can('vehicle_type_id')) p.vehicle_type_id = selectedCar?.vehicle_type_id ?? selectedCar?.id
        if (can('passenger_types')) { p.passenger_types = toPassengerArr(committedPassengers); p.passenger_count = p.passenger_types.reduce((s, x) => s + x.quantity, 0) }
        if (can('child_seats')) p.child_seats = toChildSeatArr(committedChildSeats)
        if (can('luggage')) p.luggage = toLuggageArr(committedLuggage, committedOversizedLuggage)
        if (can('discount_code')) p.discount_code = couponCode || ''
        return p
    }, [committedDestinations, selectedCar, committedPassengers, committedChildSeats, committedLuggage, committedOversizedLuggage, couponCode])

    // ── Update only (used by /edit page main submit) ──
    const doUpdate = useCallback(async () => {
        // Same date & time requirement as doConfirm — never submit a schedule-less trip.
        if (!liveDatetime?.time && !trip?.scheduled_at) {
            setBookingError('Please select the arrival date and time before updating.')
            return
        }
        setIsSubmitting(true); setBookingError(null)
        const effectiveFlightNumber = flightNumber || sharedFlightNumber || null
        const editable = trip?.edit_policy?.editable_fields || []
        const updatePayload = {}
        if (editable.includes('special_request')) {
            // Capped again at the SUBMISSION boundary, not only in the form. A trip created
            // before this cap existed carries an over-length note, and a passenger editing
            // something else entirely never opens the field that would trim it — so the last
            // thing to touch the value enforces the limit. See BOOKING_CONSTRAINTS.
            updatePayload.special_request = (specialRequest.notes || '')
                .slice(0, BOOKING_CONSTRAINTS.SPECIAL_REQUEST_MAX_LENGTH)
        }
        if (editable.includes('having_pet')) updatePayload.having_pet = specialRequest.caringPet === true
        if (editable.includes('quiet_ride')) updatePayload.quiet_ride = specialRequest.quietRide === true
        if (editable.includes('gratuity_percentage')) updatePayload.gratuity_percentage = driverTip ? parseInt(driverTip) : null
        if (editable.includes('scheduled_at') && liveDatetime?.time) updatePayload.scheduled_at = liveDatetime.time.replace('T', ' ')
        // The direction below comes from isNearAirport, which reads the airport list out of
        // the react-query cache synchronously. It is fetched at app root, so this resolves
        // from cache — it only actually waits if the user submitted faster than the request,
        // which would otherwise send an airport trip as 'others'.
        await ensureAirports()
        // Backfill any postal_code the trip GET didn't return before sending locations.
        const dests = await ensurePostalCodes(committedDestinations)
        // Direction from the destinations actually being sent (same rule doConfirm uses),
        // NOT the render-time `tripDirection` — that closure was captured before the await
        // and can disagree with the locations in this payload.
        if (editable.includes('trip_direction')) {
            updatePayload.trip_direction = directionFor(dests.origin, dests.destination)

            // The airport (and terminal) the edited route touches, matched from the SAME
            // locations the direction was decided from. Gated on the same editable field:
            // if the passenger can't change the direction they can't have moved the trip
            // to a different airport either. Omitted, not nulled, on a city-to-city route —
            // a null would read as "clear the airport" on a trip that never had one.
            const {airport_id, airport_terminal_id} = getAirportIdsFor(dests.origin, dests.destination)
            if (airport_id) updatePayload.airport_id = airport_id
            if (airport_terminal_id) updatePayload.airport_terminal_id = airport_terminal_id
        }

        // Flight number, decided AFTER the direction because the two are now coupled: the
        // API refuses to CLEAR the flight on a from-airport trip, and a switch INTO
        // from_airport needs a number in the same payload or already on the trip. Blocked
        // here rather than sent, since a null in either case can only come back 422.
        // Other directions are unchanged — clearing a to-airport flight still sends null.
        if (editable.includes('flight_number')) {
            const submittedDirection = updatePayload.trip_direction || trip?.trip_direction
            if (submittedDirection === TRIP_DIRECTIONS.FROM_AIRPORT && !effectiveFlightNumber) {
                setIsSubmitting(false)
                setBookingError('A flight number is required for rides from the airport — add the flight before saving.')
                return
            }
            updatePayload.flight_number = effectiveFlightNumber
        }

        Object.assign(updatePayload, structuralUpdateFields(editable, {dests}))
        try {
            const result = await tripService.updateTrip(rideId, updatePayload)
            if (result.success) {
                // Changing scheduled_at RE-PRICES the trip server-side (surge is time-based),
                // so final_price / pricing_breakdown in this response are the new truth. Seat
                // it rather than relying on the invalidate alone — the GET can race the recalc.
                const updated = result.data?.trip || result.data
                if (updated && typeof updated === 'object') {
                    queryClient.setQueryData(['trip', rideId], (prev) => prev ? {...prev, trip: updated} : prev)
                }
                queryClient.invalidateQueries({queryKey: ['trips']}); queryClient.invalidateQueries({queryKey: ['trip', rideId]})
                Object.keys(sessionStorage).forEach(key => { if (key.startsWith(`${EDIT_CACHE_PREFIX}${rideId}-`)) sessionStorage.removeItem(key) })
                if (effectiveFlightNumber && !flightNumber) setFlightNumber(effectiveFlightNumber)
                setPreviewOverlay(null) // edits are saved — refetch shows them from the server now
                setShowSuccess(true)
            } else if (result?.statusCode === 409 || result?.raw?.code === 409) {
                // 409 = trip changed under us (race/stale) — refetch & re-render, not an error.
                queryClient.invalidateQueries({queryKey: ['trips']}); queryClient.invalidateQueries({queryKey: ['trip', rideId]})
            } else if (isPaymentMethodUnusableError(result)) {
                // The card attached to this trip was verified on save and refused (expired,
                // deactivated, or from the other Square environment). Drop the selection and
                // refetch the list — unusable cards are filtered server-side, so the refetch
                // is what clears the bad one out of the card section.
                setSelectedCardId(null)
                queryClient.invalidateQueries({queryKey: ['payment-methods']})
                setBookingError(result.raw?.message || PAYMENT_METHOD_UNUSABLE_MESSAGE)
            } else { setBookingError(result.error || 'Server is busy, please try again') }
        } catch {
            setBookingError('Something went wrong. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }, [flightNumber, sharedFlightNumber, specialRequest, driverTip, rideId, queryClient, liveDatetime, trip, structuralUpdateFields, committedDestinations, ensurePostalCodes])

    // ── Finalize flight number (isToAirport optional capture) ──
    const finalizeFlightMutation = useMutation({
        mutationFn: () => tripService.updateTrip(rideId, {flight_number: sharedFlightNumber, without_polyline: true, trip_direction: tripDirection}),
        onSuccess: (result) => {
            if (result.success) {
                setFlightNumber(sharedFlightNumber)
                setShowFlightPrompt(false)
                setShowSuccess(true)
                setFlightFinalizeError(null)
                queryClient.invalidateQueries({queryKey: ['trip', rideId]})
            } else if (result?.statusCode === 409 || result?.raw?.code === 409) {
                queryClient.invalidateQueries({queryKey: ['trip', rideId]}); queryClient.invalidateQueries({queryKey: ['trips']})
                setShowFlightPrompt(false)
            } else {
                setFlightFinalizeError(result.error || 'Could not save flight number. Please try again.')
            }
        },
        onError: () => setFlightFinalizeError('Something went wrong. Please try again.'),
    })

    // ── Cancel ──
    const cancelMutation = useMutation({
        mutationFn: (reason) => tripService.cancelTrip(rideId, reason),
        onSuccess: (result) => {
            // 409 = trip already changed (race / already cancelled) — treat as stale, not an error.
            const stale = result?.statusCode === 409 || result?.raw?.code === 409
            // DON'T navigate here — the cancel popup shows its own "Ride Cancelled"
            // success screen and the user leaves from there (Book New Ride / ✕).
            // We only refetch so the trip reflects the cancelled state + receipt link.
            if (result.success || stale) { queryClient.invalidateQueries({queryKey: ['trips']}); queryClient.invalidateQueries({queryKey: ['trip', rideId]}) }
        },
    })

    // ── Change handlers ──
    const handleDestinationsChange = useCallback((v) => { setDestinations(typeof v === 'function' ? v : {...v}) }, [])
    const handlePassengersChange = useCallback((v) => { setPassengers(typeof v === 'function' ? v : {...v}) }, [])
    const handleChildSeatsChange = useCallback((v) => { setChildSeats(typeof v === 'function' ? v : {...v}) }, [])
    const handleLuggageChange = useCallback((v) => { setLuggage(typeof v === 'function' ? v : {...v}) }, [])
    const handleOversizedChange = useCallback((v) => { setOversizedLuggage(typeof v === 'function' ? v : {...v}) }, [])
    const toggle = useCallback((s) => setExpanded(p => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n }), [])

    // Tip change → preview recalc only (persisted on the main submit)
    const handleTipChange = useCallback(async (tipVal) => {
        setCommittedDriverTip(String(tipVal))
        return await runPreviewRecalc({tip: tipVal})
    }, [runPreviewRecalc])

    // Coupon apply → preview recalc only (persisted on the main submit)
    const handleCouponRecalculate = useCallback(async (code) => {
        setCouponCode(code || '')
        return await runPreviewRecalc({coupon: code || null})
    }, [runPreviewRecalc])

    return {
        // Data
        trip, isLoading, rideId, getSectionPolicy, getFieldPolicy, getCancelPolicy,
        // Live state
        destinations, passengers, childSeats, luggage, oversizedLuggage,
        // Committed
        committedDestinations, committedPassengers, committedChildSeats, committedLuggage, committedOversizedLuggage,
        // Vehicle
        selectedCar, setSelectedCar, committedCarTypeId, availableCars,
        // Other
        selectedCardId, setSelectedCardId, savedCards, setSavedCards, specialRequest, setSpecialRequest,
        contact,
        driverTip, setDriverTip, flightNumber, couponCode,
        // Selectables
        passengerTypes, childSeatTypes, luggageTypes,
        // UI
        editingSection, passengerView, setPassengerView, passengerOnly, destinationPart, luggageOversizedMode, setLuggageOversizedMode, carBreakdownId, setCarBreakdownId, isSubmitting, isRecalculating, sectionSaveError, bookingError, showSuccess,
        expanded, confirmStep, setConfirmStep, showCancelPopup, setShowCancelPopup, showSRPopup, setShowSRPopup, infoPopup, setInfoPopup,
        showFlightPrompt, setShowFlightPrompt, flightFinalizeError, finalizeFlightMutation,
        // DateTime
        liveDatetime, setLiveDatetime, minLeadTimeError,
        // Flight
        sharedFlightNumber, setSharedFlightNumber, sharedFlightDetails, setSharedFlightDetails,
        sharedFlightLoading, setSharedFlightLoading, sharedFlightError, setSharedFlightError,
        sharedFlightUnavailable, setSharedFlightUnavailable,
        sharedTimeApplied, setSharedTimeApplied, hasFlightNumber, handleFlightNumberChange, clearFlight,
        // Hints
        dateHint, setDateHint, flightHint, setFlightHint, smartTimePopup, setSmartTimePopup, closeSmartTime, setFlightDropOnClose,
        skipHintTransition, dateTimeSectionRef, applySmartTimeRef,
        // Airport
        isFromAirport, isToAirport, isAirportTrip, tripDirection,
        // Handlers
        handleDateChange, handleTimeChange, handleSmartTimeSelect, openEdit, closeEdit,
        handleSectionDone, doUpdate, cancelMutation, toggle,
        handleDestinationsChange, handlePassengersChange, handleChildSeatsChange, handleLuggageChange, handleOversizedChange,
        handleTipChange, handleCouponRecalculate,
    }
}
